/**
 * CREATIVE DETONATOR - Frontend Pricing Configuration
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │  SINGLE SOURCE OF TRUTH for all credit costs displayed in the UI       │
 * │  These values MUST match backend/lib/pricing.js                        │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * 1 Credit = $0.01 (1 cent)
 *
 * HOW TO UPDATE PRICES:
 * =====================
 * 1. Update backend/lib/pricing.js FIRST (this is the authoritative source)
 * 2. Update THIS file to match the backend values
 * 3. Run the app and verify all modals show correct costs
 *
 * FILES THAT USE THIS MODULE:
 * ===========================
 * - frontend/features/generator/features/image-generation.js (Generate Image modal)
 * - frontend/features/generator/features/image-edit.js (Edit Image modal)
 * - frontend/features/generator/features/video-generation.js (Generate Video modal)
 * - frontend/features/generator/phases/phase2-arming.js (Detonation credit counter)
 * - frontend/features/generator/ui/idea-card.js (Generate Image buttons)
 * - frontend/js/shared/idea-canvas/refine-form.js (Art Director refinement)
 *
 * PRICING SUMMARY:
 * ================
 * TEXT GENERATION:
 *   - Standard (Sonnet, GPT-4o): 10 credits
 *   - Deep (Opus, o1): 35 credits
 *   - Fast (Haiku, mini): 1 credit
 *   - Refinement: 3 credits
 *   - Format conversion: 3 credits
 *
 * IMAGE GENERATION:
 *   - Flux2 Flash: 1 credit (auto-only)
 *   - SeedReam: 5 credits (default)
 *   - Nano Banana 1K: 20 credits
 *   - Nano Banana 4K: 40 credits
 *   - Image Edit: 20 credits
 *
 * VIDEO GENERATION:
 *   - Veo (no audio): 25 credits/sec
 *   - Veo (with audio): 50 credits/sec
 *   - ByteDance: 40 credits base
 */

// ============================================================================
// CREDIT COSTS - Must match backend/lib/pricing.js PRICING object
// ============================================================================

/**
 * Text generation costs (per API call)
 */
export const TEXT_COSTS = {
  standard: 10,      // Claude Sonnet, GPT-4o
  deep: 35,          // Claude Opus, o1
  fast: 1,           // GPT-4o-mini, Haiku
  refinement: 3,     // Art Director text refinement
  video_scenes: 5,   // Video scene AI generation
  format_conversion: 3  // Format conversion
};

/**
 * Image generation costs (per image)
 */
export const IMAGE_COSTS = {
  'flux2-flash': 1,      // $0.005/image - AUTO-ONLY (fast/cheap)
  'seedream': 5,         // $0.04/image - Default provider
  'nano-banana': 20,     // $0.15/image - Premium 1K
  'nano-banana-4k': 40,  // $0.30/image - Premium 4K
  'edit': 20,            // $0.15/edit - Image editing
  'scene_frame': 5       // Scene keyframe (uses SeedReam)
};

/**
 * Video generation costs
 * Veo: Per-second pricing
 * ByteDance: Base rate with multipliers
 */
export const VIDEO_COSTS = {
  // Google Veo 3.1
  veo_per_sec_audio: 50,      // $0.40/sec with audio
  veo_per_sec_no_audio: 25,   // $0.20/sec without audio

  // ByteDance Seedance
  bytedance_base: 40          // ~$0.30 base rate
};

// ============================================================================
// COST CALCULATION HELPERS
// ============================================================================

/**
 * Get image generation cost by provider
 * @param {string} provider - Provider ID (seedream, nano-banana, flux2-flash)
 * @param {string} resolution - Resolution (1K, 4K) - only affects nano-banana
 * @returns {number} Cost in credits
 */
export function getImageCost(provider, resolution = '1K') {
  if (!provider) return IMAGE_COSTS['seedream'];

  const providerLower = provider.toLowerCase();

  if (providerLower.includes('flux')) {
    return IMAGE_COSTS['flux2-flash'];
  }

  if (providerLower.includes('nano') || providerLower.includes('banana')) {
    return resolution === '4K' ? IMAGE_COSTS['nano-banana-4k'] : IMAGE_COSTS['nano-banana'];
  }

  // Default to seedream
  return IMAGE_COSTS['seedream'];
}

/**
 * Get image edit cost
 * @returns {number} Cost in credits (always 20)
 */
export function getImageEditCost() {
  return IMAGE_COSTS['edit'];
}

/**
 * Get video generation cost
 * @param {Object} options - Video options
 * @param {string} options.provider - Provider (veo, bytedance)
 * @param {number} options.duration - Duration in seconds
 * @param {boolean} options.generateAudio - Whether audio is enabled
 * @param {string} options.resolution - Resolution (480p, 720p, 1080p)
 * @returns {number} Cost in credits
 */
export function getVideoCost(options = {}) {
  const {
    provider = 'veo',
    duration = 8,
    generateAudio = true,
    resolution = '720p'
  } = options;

  const providerLower = provider.toLowerCase();

  // ByteDance Seedance
  if (providerLower.includes('bytedance') || providerLower.includes('seedance')) {
    const resolutionMultiplier = resolution === '480p' ? 0.5 : resolution === '1080p' ? 2 : 1;
    const durationMultiplier = duration / 5; // Base is 5s
    const audioMultiplier = generateAudio ? 2 : 1;
    return Math.ceil(VIDEO_COSTS.bytedance_base * resolutionMultiplier * durationMultiplier * audioMultiplier);
  }

  // Default: Google Veo per-second pricing
  const perSecondRate = generateAudio
    ? VIDEO_COSTS.veo_per_sec_audio
    : VIDEO_COSTS.veo_per_sec_no_audio;

  return duration * perSecondRate;
}

/**
 * Get text generation cost by model type
 * @param {string} model - Model name/ID
 * @returns {number} Cost in credits
 */
export function getTextCost(model) {
  if (!model) return TEXT_COSTS.standard;

  const modelLower = model.toLowerCase();

  // Deep/Premium models
  if (modelLower.includes('opus') ||
      modelLower.includes('o1') ||
      modelLower.includes('o3') ||
      modelLower.includes('gpt-5')) {
    return TEXT_COSTS.deep;
  }

  // Fast/Cheap models
  if (modelLower.includes('mini') ||
      modelLower.includes('haiku') ||
      modelLower.includes('flash')) {
    return TEXT_COSTS.fast;
  }

  // Standard models (Sonnet, GPT-4o, etc.)
  return TEXT_COSTS.standard;
}

/**
 * Calculate total generation cost (text + optional auto-images)
 * @param {Object} options - Generation options
 * @param {number} options.triggerCount - Number of triggers
 * @param {number} options.formatCount - Number of formats
 * @param {number} options.ideasPerTrigger - Ideas per trigger (default 3)
 * @param {boolean} options.autoImageEnabled - Auto-image generation enabled
 * @param {string} options.autoImageProvider - Image provider for auto-gen
 * @param {string[]} options.autoImageFormats - Formats to auto-generate images for
 * @param {string[]} options.selectedFormats - Actually selected formats
 * @param {string} options.model - AI model for text generation
 * @returns {Object} { textCredits, imageCredits, totalCredits }
 */
export function calculateGenerationCost(options = {}) {
  const {
    triggerCount = 0,
    formatCount = 0,
    ideasPerTrigger = 3,
    autoImageEnabled = false,
    autoImageProvider = 'seedream',
    autoImageFormats = ['print'],
    selectedFormats = [],
    model = 'standard'
  } = options;

  // Text generation cost
  const textCostPerCall = getTextCost(model);
  const textCredits = triggerCount * formatCount * textCostPerCall;

  // Image generation cost (if auto-image enabled)
  let imageCredits = 0;
  if (autoImageEnabled && selectedFormats.length > 0) {
    const visualFormats = ['print', 'social', 'digital', 'experiential'];

    // Count formats that are visual AND in auto-image list
    const autoVisualCount = selectedFormats.filter(f =>
      visualFormats.includes(f) && autoImageFormats.includes(f)
    ).length;

    // Total images = triggers × ideas × visual formats
    const totalImages = triggerCount * ideasPerTrigger * autoVisualCount;
    const imageCostPerUnit = getImageCost(autoImageProvider);
    imageCredits = totalImages * imageCostPerUnit;
  }

  return {
    textCredits,
    imageCredits,
    totalCredits: textCredits + imageCredits
  };
}

// ============================================================================
// USD CONVERSION (for display purposes only)
// ============================================================================

/**
 * Convert credits to USD string
 * @param {number} credits - Credit amount
 * @returns {string} USD string like "$1.50"
 */
export function creditsToUSD(credits) {
  const usd = credits / 100;
  return `$${usd.toFixed(2)}`;
}

/**
 * Format credits for display
 * @param {number} credits - Credit amount
 * @param {boolean} includeUSD - Include USD equivalent
 * @returns {string} Formatted string like "20 C" or "$0.20 (20 C)"
 */
export function formatCredits(credits, includeUSD = false) {
  if (includeUSD) {
    return `${creditsToUSD(credits)} (${credits} C)`;
  }
  return `${credits} C`;
}

// ============================================================================
// EXPORTS FOR BACKWARDS COMPATIBILITY
// ============================================================================

// Legacy alias used by some modules
export const CREDIT_COSTS = IMAGE_COSTS;

// Default export for convenient imports
export default {
  TEXT_COSTS,
  IMAGE_COSTS,
  VIDEO_COSTS,
  getImageCost,
  getImageEditCost,
  getVideoCost,
  getTextCost,
  calculateGenerationCost,
  creditsToUSD,
  formatCredits
};
